import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { createPortal } from 'react-dom';
import styles from "./ColorPicker.module.css";

const ColorPicker = ({ label, id, value: initialValue = "#333333", onChange, defaultColor = null }) => {
  const [color, setColor] = useState(initialValue);
  const [showPicker, setShowPicker] = useState(false);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);

  // Get the default color to use for reset
  const getDefaultColor = () => {
    return defaultColor || initialValue || "#333333";
  };

  // Update internal color when prop changes
  useEffect(() => {
    setColor(initialValue);
  }, [initialValue, label]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calculate position when picker opens
  useEffect(() => {
    if (showPicker && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      const pickerHeight = 430; // Slightly increased to accommodate reset button
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top;
      // Open upward if not enough space below, otherwise open downward
      if (spaceBelow < pickerHeight && spaceAbove > pickerHeight) {
        // Position above the trigger
        top = rect.top + scrollTop - pickerHeight - 8;
      } else {
        // Position below the trigger
        top = rect.bottom + scrollTop + 8;
      }
      
      setPortalPosition({
        top: top,
        left: rect.left + scrollLeft + 55 // 55px offset for the color preview square
      });
    }
  }, [showPicker]);

  const handleChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor);
    if (onChange) onChange(newColor);
  };

  const handleColorPreviewClick = () => {
    setShowPicker(!showPicker);
  };

  const handlePresetColorClick = (presetColor) => {
    setColor(presetColor);
    if (onChange) onChange(presetColor);
    setShowPicker(false);
  };

  const handleResetToDefault = () => {
    const defaultColorValue = getDefaultColor();
    setColor(defaultColorValue);
    if (onChange) onChange(defaultColorValue);
    setShowPicker(false);
  };

  // Common color presets
  const colorPresets = [
    "#333333", "#FFFFFF", "#FF0000", "#00FF00", "#0000FF", 
    "#FFFF00", "#00FFFF", "#FF00FF", "#C0C0C0", "#808080",
    "#800000", "#808000", "#008000", "#800080", "#008080", 
    "#000080", "#FFA500", "#FFC0CB", "#FFD700", "#ADD8E6"
  ];

  return (
    <div className={styles["hvnly_settings_api__form-group"]}>
      {label && (
        <label htmlFor={id} className={styles["hvnly_settings_api__form-label"]}>
          {label}
        </label>
      )}

      <div className={styles["hvnly_settings_api__color-picker"]}>
        <div
          ref={triggerRef}
          className={styles["hvnly_settings_api__color-preview"]}
          style={{ backgroundColor: color }}
          onClick={handleColorPreviewClick}
        ></div>
        <input
          type="text"
          id={id}
          className={styles["hvnly_settings_api__form-control"]}
          value={color}
          onChange={handleChange}
          placeholder={__("Enter hex color", "havenlytics")}
        />
        
        {showPicker && createPortal(
          <div 
            ref={pickerRef}
            className={styles["hvnly_settings_api__color-palette-portal"]}
            style={{
              position: 'absolute',
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              zIndex: 9999999
            }}
          >
            <div className={styles["hvnly_settings_api__color-palette-header"]}>
              <span>{__("Select a color", "havenlytics")}</span>
              <button 
                onClick={() => setShowPicker(false)}
                className={styles["hvnly_settings_api__color-palette-close"]}
              >
                ×
              </button>
            </div>
            
            <input
              type="color"
              value={color}
              onChange={handleChange}
              className={styles["hvnly_settings_api__color-input"]}
            />
            
            <div className={styles["hvnly_settings_api__color-presets"]}>
              {colorPresets.map((presetColor, index) => (
                <div
                  key={index}
                  className={styles["hvnly_settings_api__color-preset"]}
                  style={{ backgroundColor: presetColor }}
                  onClick={() => handlePresetColorClick(presetColor)}
                />
              ))}
            </div>
            
            <div className={styles["hvnly_settings_api__color-reset-container"]}>
              <button
                type="button"
                onClick={handleResetToDefault}
                className={styles["hvnly_settings_api__color-reset-btn"]}
              >
                <i className="fas fa-undo-alt"></i>
                {__("Reset to Default", "havenlytics")}
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ColorPicker;